/**
 * TransitionIdGenerator - Single source of truth for ALL synthetic IDs related to entity structure
 *
 * This service generates and parses two types of IDs for both screens and blocks:
 *
 * 1. Transition IDs (edges/connections between entities)
 *    Format: transition:{sourceEntityId}:action:{actionKey}:target:{targetEntityId}
 *    Examples:
 *    - transition:abc-123-def:action:footer.button1:target:xyz-456-789
 *    - transition:abc-123-def:action:list.item.0:target:xyz-456-789
 *    - transition:block-1:action:action:target:block-2
 *
 * 2. Handle IDs (visual connection points on nodes)
 *    Format: handle:{entityId}:action:{actionKey}
 *    Examples:
 *    - handle:abc-123-def:action:footer.button1
 *    - handle:abc-123-def:action:list.item.0
 *    - handle:block-1:action:action
 *
 * Features:
 * - Deterministic (same inputs → same ID)
 * - Stable (ID doesn't change when other entities are added/removed)
 * - Parseable (easy to extract any part)
 * - Extensible (can add more ID types in future)
 * - Self-documenting (parts are named)
 *
 * Usage:
 * ```typescript
 * // Generate transition ID
 * const transitionId = TransitionIdGenerator.generateTransition('screen1', 'footer.button1', 'screen2');
 * // → "transition:screen1:action:footer.button1:target:screen2"
 *
 * // Generate handle ID
 * const handleId = TransitionIdGenerator.generateHandleId('screen1', 'footer.button1');
 * // → "handle:screen1:action:footer.button1"
 *
 * // Parse transition ID
 * const parts = TransitionIdGenerator.parseTransition(transitionId);
 * // → { sourceScreenId: 'screen1', sourceActionKey: 'footer.button1', targetScreenId: 'screen2' }
 *
 * // Parse handle ID
 * const handleParts = TransitionIdGenerator.parseHandleId(handleId);
 * // → { screenId: 'screen1', actionKey: 'footer.button1' }
 * ```
 *
 * Related: CreateEdgeCommand, ScreenTransitionManager, ScreenConverterService, FlowCanvasCoordinator, FlowScreenNode
 */

/**
 * Parsed transition ID components
 */
export interface ParsedTransitionId {
  sourceEntityId: string;
  sourceActionKey: string;
  targetEntityId: string;
}

/**
 * Parsed handle ID components
 */
export interface ParsedHandleId {
  entityId: string;
  actionKey: string;
}

/**
 * Transition ID generation and parsing service
 */
export class TransitionIdGenerator {
  // Common constants
  private static readonly SEPARATOR = ':';
  private static readonly ACTION_MARKER = 'action';

  // Transition ID constants
  private static readonly TRANSITION_PREFIX = 'transition';
  private static readonly TARGET_MARKER = 'target';

  // Handle ID constants
  private static readonly HANDLE_PREFIX = 'handle';

  // Regex for validation and parsing
  // Transition format: transition:{sourceEntityId}:action:{actionKey}:target:{targetEntityId}
  // Note: actionKey can contain dots (e.g., list.item.0), so we use [^:]+ to match everything except separator
  private static readonly TRANSITION_REGEX = /^transition:([^:]+):action:([^:]+):target:(.+)$/;

  // Handle format: handle:{entityId}:action:{actionKey}
  private static readonly HANDLE_REGEX = /^handle:([^:]+):action:(.+)$/;

  /**
   * Generate deterministic transition ID
   *
   * @param sourceEntityId - ID of source entity (screen or block)
   * @param sourceActionKey - Action key (e.g., 'footer.button1', 'list.item.0', 'action')
   * @param targetEntityId - ID of target entity (screen or block)
   * @returns Formatted transition ID
   *
   * @example
   * generate('abc-123', 'footer.button1', 'xyz-789')
   * // → "transition:abc-123:action:footer.button1:target:xyz-789"
   *
   * generate('block-1', 'action', 'block-2')
   * // → "transition:block-1:action:action:target:block-2"
   */
  static generate(
    sourceEntityId: string,
    sourceActionKey: string,
    targetEntityId: string
  ): string {
    // Validate inputs
    if (!sourceEntityId || !sourceActionKey || !targetEntityId) {
      throw new Error(
        `[TransitionIdGenerator] Invalid inputs: sourceEntityId="${sourceEntityId}", ` +
        `sourceActionKey="${sourceActionKey}", targetEntityId="${targetEntityId}"`
      );
    }

    // Check for separator in inputs (would break parsing)
    const separator = this.SEPARATOR;
    if (
      sourceEntityId.includes(separator) ||
      sourceActionKey.includes(separator) ||
      targetEntityId.includes(separator)
    ) {
      throw new Error(
        `[TransitionIdGenerator] Inputs cannot contain separator "${separator}": ` +
        `sourceEntityId="${sourceEntityId}", sourceActionKey="${sourceActionKey}", targetEntityId="${targetEntityId}"`
      );
    }

    return [
      this.TRANSITION_PREFIX,
      sourceEntityId,
      this.ACTION_MARKER,
      sourceActionKey,
      this.TARGET_MARKER,
      targetEntityId,
    ].join(separator);
  }

  /**
   * Parse transition ID into components
   *
   * @param transitionId - Transition ID to parse
   * @returns Parsed components
   * @throws Error if ID format is invalid
   *
   * @example
   * parse('transition:abc-123:action:footer.button1:target:xyz-789')
   * // → { sourceEntityId: 'abc-123', sourceActionKey: 'footer.button1', targetEntityId: 'xyz-789' }
   */
  static parse(transitionId: string): ParsedTransitionId {
    const match = this.TRANSITION_REGEX.exec(transitionId);

    if (!match) {
      throw new Error(
        `[TransitionIdGenerator] Invalid transition ID format: "${transitionId}". ` +
        `Expected format: transition:{sourceEntityId}:action:{actionKey}:target:{targetEntityId}`
      );
    }

    return {
      sourceEntityId: match[1],
      sourceActionKey: match[2],
      targetEntityId: match[3],
    };
  }

  /**
   * Validate transition ID format
   *
   * @param transitionId - ID to validate
   * @returns True if ID is valid
   *
   * @example
   * isValid('transition:abc-123:action:footer.button1:target:xyz-789') // → true
   * isValid('invalid-id-format') // → false
   */
  static isValid(transitionId: string): boolean {
    return this.TRANSITION_REGEX.test(transitionId);
  }

  /**
   * Extract source entity ID from transition ID
   *
   * @param transitionId - Transition ID
   * @returns Source entity ID
   * @throws Error if ID format is invalid
   */
  static getSourceEntityId(transitionId: string): string {
    return this.parse(transitionId).sourceEntityId;
  }

  /**
   * Extract action key from transition ID
   *
   * @param transitionId - Transition ID
   * @returns Action key (e.g., 'footer.button1', 'list.item.0', 'action')
   * @throws Error if ID format is invalid
   */
  static getActionKey(transitionId: string): string {
    return this.parse(transitionId).sourceActionKey;
  }

  /**
   * Extract target entity ID from transition ID
   *
   * @param transitionId - Transition ID
   * @returns Target entity ID
   * @throws Error if ID format is invalid
   */
  static getTargetEntityId(transitionId: string): string {
    return this.parse(transitionId).targetEntityId;
  }

  /**
   * Check if transition connects specific entities
   *
   * Useful for filtering transitions when deleting entities
   *
   * @param transitionId - Transition ID to check
   * @param sourceEntityId - Source entity ID (optional)
   * @param targetEntityId - Target entity ID (optional)
   * @returns True if transition matches criteria
   *
   * @example
   * // Check if transition goes FROM entity1
   * connects(id, 'entity1', undefined) // → true if source is entity1
   *
   * // Check if transition goes TO entity2
   * connects(id, undefined, 'entity2') // → true if target is entity2
   *
   * // Check if transition connects entity1 → entity2
   * connects(id, 'entity1', 'entity2') // → true if exact match
   */
  static connects(
    transitionId: string,
    sourceEntityId?: string,
    targetEntityId?: string
  ): boolean {
    try {
      const parsed = this.parse(transitionId);

      const sourceMatch = !sourceEntityId || parsed.sourceEntityId === sourceEntityId;
      const targetMatch = !targetEntityId || parsed.targetEntityId === targetEntityId;

      return sourceMatch && targetMatch;
    } catch {
      return false;
    }
  }

  // ============================================================================
  // Handle ID Methods
  // ============================================================================

  /**
   * Generate deterministic handle ID
   *
   * Format: handle:{entityId}:action:{actionKey}
   *
   * @param entityId - ID of the entity (screen or block)
   * @param actionKey - Action key (e.g., 'footer.button1', 'list.item.0', 'action')
   * @returns Formatted handle ID
   *
   * @example
   * generateHandleId('abc-123', 'footer.button1')
   * // → "handle:abc-123:action:footer.button1"
   *
   * generateHandleId('block-1', 'action')
   * // → "handle:block-1:action:action"
   */
  static generateHandleId(entityId: string, actionKey: string): string {
    // Validate inputs
    if (!entityId || !actionKey) {
      throw new Error(
        `[TransitionIdGenerator] Invalid inputs for handle ID: entityId="${entityId}", actionKey="${actionKey}"`
      );
    }

    // Check for separator in inputs (would break parsing)
    const separator = this.SEPARATOR;
    if (entityId.includes(separator) || actionKey.includes(separator)) {
      throw new Error(
        `[TransitionIdGenerator] Handle ID inputs cannot contain separator "${separator}": ` +
        `entityId="${entityId}", actionKey="${actionKey}"`
      );
    }

    return [
      this.HANDLE_PREFIX,
      entityId,
      this.ACTION_MARKER,
      actionKey,
    ].join(separator);
  }

  /**
   * Parse handle ID into components
   *
   * @param handleId - Handle ID to parse
   * @returns Parsed components
   * @throws Error if ID format is invalid
   *
   * @example
   * parseHandleId('handle:abc-123:action:footer.button1')
   * // → { entityId: 'abc-123', actionKey: 'footer.button1' }
   */
  static parseHandleId(handleId: string): ParsedHandleId {
    const match = this.HANDLE_REGEX.exec(handleId);

    if (!match) {
      throw new Error(
        `[TransitionIdGenerator] Invalid handle ID format: "${handleId}". ` +
        `Expected format: handle:{entityId}:action:{actionKey}`
      );
    }

    return {
      entityId: match[1],
      actionKey: match[2],
    };
  }

  /**
   * Validate handle ID format
   *
   * @param handleId - ID to validate
   * @returns True if ID is valid
   *
   * @example
   * isValidHandleId('handle:abc-123:action:footer.button1') // → true
   * isValidHandleId('invalid-id-format') // → false
   */
  static isValidHandleId(handleId: string): boolean {
    return this.HANDLE_REGEX.test(handleId);
  }

  /**
   * Extract entity ID from handle ID
   *
   * @param handleId - Handle ID
   * @returns Entity ID
   * @throws Error if ID format is invalid
   *
   * @example
   * getEntityIdFromHandle('handle:abc-123:action:footer.button1')
   * // → 'abc-123'
   */
  static getEntityIdFromHandle(handleId: string): string {
    return this.parseHandleId(handleId).entityId;
  }

  /**
   * Extract action key from handle ID
   *
   * @param handleId - Handle ID
   * @returns Action key
   * @throws Error if ID format is invalid
   *
   * @example
   * getActionKeyFromHandle('handle:abc-123:action:footer.button1')
   * // → 'footer.button1'
   */
  static getActionKeyFromHandle(handleId: string): string {
    return this.parseHandleId(handleId).actionKey;
  }

  /**
   * Check if handle belongs to specific entity
   *
   * @param handleId - Handle ID to check
   * @param entityId - Entity ID to match
   * @returns True if handle belongs to entity
   *
   * @example
   * belongsToEntity('handle:abc-123:action:footer.button1', 'abc-123') // → true
   * belongsToEntity('handle:abc-123:action:footer.button1', 'xyz-456') // → false
   */
  static belongsToEntity(handleId: string, entityId: string): boolean {
    try {
      const parsed = this.parseHandleId(handleId);
      return parsed.entityId === entityId;
    } catch {
      return false;
    }
  }
}

// Export singleton for convenience (though all methods are static)
export const transitionIdGenerator = TransitionIdGenerator;
